import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "@/Portal.css";
import PortalMessages from "@/components/PortalMessages";
import { WalkthroughBanner, FirstLoginTour, UnreadTriggerBadge } from "@/components/OwnerWalkthrough";

const BACKEND_URL = "";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c45d5dc5-1aa9-4fda-8179-43802ae25660/artifacts/r7bevoxt_1210928841187418.jpg";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleLogin = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Auth Callback — processes session_id from URL hash
export const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
    if (!sessionId) { navigate("/login"); return; }

    // Small retry helper — session cookie propagation on cross-site preview
    // subdomains can lag by a hop. Retry /auth/me up to 3 times over ~1.2s
    // before declaring the cookie blocked.
    const meWithRetry = async (attempts = 3) => {
      for (let i = 0; i < attempts; i++) {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        if (r.ok) return r.json();
        if (i < attempts - 1) await new Promise((res) => setTimeout(res, 400));
      }
      throw new Error("cookie_not_persisted");
    };

    fetch(`${BACKEND_URL}/api/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("session_failed");
        return r.json();
      })
      .then(() => meWithRetry(3))
      .then((user) => {
        const from = localStorage.getItem("mf_auth_redirect") || "";
        localStorage.removeItem("mf_auth_redirect");
        const role = user.role || "";
        const dashRole = user.dashboard_role || "";
        const email = (user.email || "").toLowerCase();
        // STRICT CEO ISOLATION: only these two emails go to the CEO Cockpit.
        const CEO_EMAILS = ["nivoomforward@gmail.com", "slatricep@gmail.com"];
        const isCeo = CEO_EMAILS.includes(email);
        const isTeamAdmin = isCeo || role === "team" || dashRole === "ceo" || dashRole === "coo" || dashRole === "administrator";
        // Standard same-window navigation — using window.top caused mobile
        // Chrome to interpret the callback as a nested cross-origin frame
        // and loop the preview page. Same-window replace is safe here.
        const nav = (path) => { window.location.replace(path); };
        if (from === "dashboard" || isTeamAdmin) {
          nav("/dashboard");
        } else if (role === "crew" || role === "client") {
          nav("/crew");
        } else {
          nav("/portal");
        }
      })
      .catch((err) => {
        if (err.message === "cookie_not_persisted") {
          navigate("/login?auth_blocked=1");
        } else {
          navigate("/login");
        }
      });
  }, [navigate]);

  return <div className="portal-loading">Signing you in&hellip;</div>;
};

// Login Page
export const LoginPage = () => {
  const authBlocked = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("auth_blocked") === "1";

  useEffect(() => {
    // Already authenticated? Route to the right place based on role.
    // Uses same-window navigation — the previous window.top redirect
    // was tripping mobile Chrome into a nested-frame preview loop.
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((u) => {
        if (!u) return;
        const role = u.role || "";
        const dashRole = u.dashboard_role || "";
        const email = (u.email || "").toLowerCase();
        const CEO_EMAILS = ["nivoomforward@gmail.com", "slatricep@gmail.com"];
        const isCeo = CEO_EMAILS.includes(email);
        const isTeamAdmin = isCeo || role === "team" || dashRole === "ceo" || dashRole === "coo" || dashRole === "administrator";
        const nav = (path) => { window.location.href = path; };
        if (isTeamAdmin) {
          nav("/dashboard");
        } else if (role === "crew" || role === "client") {
          nav("/crew");
        } else {
          nav("/portal");
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-card">
        <img src={LOGO_URL} alt="Moovin Forward" className="login-logo" />
        <h1>Sign In</h1>
        <p>One door. Clients see project status &amp; invoices. Team members (Shannon &amp; Shaneka) go straight to the Owner Dashboard. Same button for everybody.</p>

        {authBlocked && (
          <div data-testid="auth-blocked-banner" style={{
            background: "rgba(255, 107, 53, 0.12)",
            border: "1px solid rgba(255, 107, 53, 0.4)",
            color: "#FF8B5A",
            padding: "12px 14px",
            borderRadius: 8,
            margin: "10px 0 18px",
            fontSize: "0.85rem",
            lineHeight: 1.5,
            textAlign: "left"
          }}>
            <strong style={{ display: "block", marginBottom: 6 }}>Your browser is blocking the sign-in cookie.</strong>
            Open this in <strong>Chrome</strong> (not Safari, not Edge), then try again. If you&apos;re still stuck:
            <ol style={{ margin: "8px 0 0 18px", padding: 0 }}>
              <li>Click the small icon at the left of the address bar (the lock or eye-with-slash)</li>
              <li>Allow cookies / third-party cookies for moovinforward.org</li>
              <li>Refresh and try again</li>
            </ol>
          </div>
        )}
        <button onClick={handleLogin} className="google-btn" data-testid="google-login-btn">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Sign in with Google
        </button>
        <p className="login-footnote" style={{ marginTop: 16, fontSize: "0.75rem", opacity: 0.6 }}>
          Use the same Gmail address registered for your role (Shannon: nivoomforward@gmail.com &middot; Shaneka: slatricep@gmail.com).
        </p>
      </div>
    </div>
  );
};

// Status Badge
const StatusBadge = ({ status }) => {
  const colors = {
    "Submitted": "#3b82f6",
    "Audit Scheduled": "#8b5cf6",
    "In Progress": "#f59e0b",
    "Marketing Live": "#06b6d4",
    "Tenant Placed": "#22c55e",
    "Complete": "#22c55e",
  };
  return (
    <span className="status-badge" style={{ background: `${colors[status] || '#64748b'}22`, color: colors[status] || '#64748b', borderColor: `${colors[status] || '#64748b'}44` }} data-testid="status-badge">
      {status}
    </span>
  );
};

// Portal Page
const Portal = () => {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [newProperty, setNewProperty] = useState({
    address: "", city: "", unit_number: "", property_type: "", monthly_rent: "", notes: ""
  });
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [shareRole, setShareRole] = useState("leasing_agent");
  const [sharing, setSharing] = useState(false);
  const [showServices, setShowServices] = useState(() => !localStorage.getItem("mf_services_seen"));
  const [propNotes, setPropNotes] = useState({ entry_code: "", alarm_code: "", pets: "", parking_instructions: "", special_instructions: "" });
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((u) => {
        // Admins/team belong on the admin dashboard, not the client portal
        const role = u.role || "";
        const dashRole = u.dashboard_role || "";
        if (role === "admin" || role === "team" || dashRole === "ceo" || dashRole === "coo") {
          navigate("/dashboard");
          return;
        }
        setUser(u); loadProjects();
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const loadProjects = () => {
    fetch(`${BACKEND_URL}/api/projects`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.own !== undefined) {
          setProjects([...data.own, ...data.shared.map(p => ({ ...p, _isShared: true }))]);
        } else {
          setProjects(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadProjectDetail = (projectId) => {
    fetch(`${BACKEND_URL}/api/projects/${projectId}`, { credentials: "include" })
      .then((r) => r.json())
      .then(setSelectedProject)
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/client/property-notes/${projectId}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : {})
      .then(n => { setPropNotes({ entry_code: n.entry_code || "", alarm_code: n.alarm_code || "", pets: n.pets || "", parking_instructions: n.parking_instructions || "", special_instructions: n.special_instructions || "" }); setNotesLoaded(true); })
      .catch(() => setNotesLoaded(true));
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    const resp = await fetch(`${BACKEND_URL}/api/projects`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...newProperty, monthly_rent: parseFloat(newProperty.monthly_rent) || 0 }),
    });
    if (resp.ok) {
      setShowAddForm(false);
      setNewProperty({ address: "", city: "", unit_number: "", property_type: "", monthly_rent: "", notes: "" });
      loadProjects();
    }
  };

  const handleLogout = async () => {
    await fetch(`${BACKEND_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login");
  };

  const handleShareAccess = async () => {
    if (!shareEmail.trim() || !selectedProject) return;
    setSharing(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/projects/${selectedProject.project_id}/share`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: shareEmail.trim(), role: shareRole })
      });
      if (r.ok) {
        setShareEmail("");
        loadProjectDetail(selectedProject.project_id);
      }
    } catch (e) { console.error("Share error:", e); }
    setSharing(false);
  };

  const handleRevokeAccess = async (shareId) => {
    if (!selectedProject) return;
    await fetch(`${BACKEND_URL}/api/projects/${selectedProject.project_id}/share/${shareId}`, {
      method: "DELETE", credentials: "include"
    });
    loadProjectDetail(selectedProject.project_id);
  };

  const savePropertyNotes = async () => {
    if (!selectedProject) return;
    setNotesSaving(true);
    await fetch(`${BACKEND_URL}/api/client/property-notes`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ project_id: selectedProject.project_id, ...propNotes })
    });
    setNotesSaving(false);
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 3000);
  };

  // Check for payment return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentResult = params.get("payment");
    if (sessionId && paymentResult === "success") {
      setPaymentStatus("checking");
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 8) { setPaymentStatus("timeout"); return; }
        try {
          const r = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`, { credentials: "include" });
          if (r.ok) {
            const data = await r.json();
            if (data.payment_status === "paid") {
              setPaymentStatus("paid");
              loadProjects();
              window.history.replaceState({}, "", "/portal");
              return;
            }
          }
        } catch (e) { /* retry */ }
        setTimeout(() => pollStatus(attempts + 1), 2500);
      };
      pollStatus();
    } else if (paymentResult === "cancelled") {
      setPaymentStatus("cancelled");
      window.history.replaceState({}, "", "/portal");
      setTimeout(() => setPaymentStatus(null), 4000);
    }
  }, []);

  const handlePayInvoice = async (invoice) => {
    setPayingInvoice(invoice.invoice_id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ invoice_id: invoice.invoice_id, origin_url: window.location.origin })
      });
      if (r.ok) {
        const data = await r.json();
        window.location.href = data.url;
      }
    } catch (e) { console.error("Payment error:", e); }
    setPayingInvoice(null);
  };

  if (loading) return <div className="portal-loading">Loading...</div>;
  if (!user) return null;

  return (
    <div className="portal" data-testid="portal-page">
      <nav className="portal-nav">
        <div className="portal-nav-left">
          <img src={LOGO_URL} alt="Moovin Forward" className="portal-logo" />
          <span className="portal-brand">Client Portal</span>
        </div>
        <div className="portal-nav-right">
          <span className="portal-user" data-testid="portal-user">{user.name || user.email}</span>
          <button onClick={handleLogout} className="portal-logout" data-testid="logout-btn">Sign Out</button>
        </div>
      </nav>

      <main className="portal-main">
        {user && <FirstLoginTour userName={user.name || ""} />}
        {!selectedProject && <UnreadTriggerBadge />}
        {selectedProject ? (
          // Project Detail View
          <div className="project-detail" data-testid="project-detail">
            <button onClick={() => setSelectedProject(null)} className="back-btn" data-testid="back-to-projects">{"\u2190"} Back to Projects</button>
            <div className="detail-header">
              <div>
                <h1 data-testid="detail-address">{selectedProject.address}{selectedProject.unit_number ? `, Unit ${selectedProject.unit_number}` : ""}</h1>
                <p className="detail-meta">{selectedProject.city} &middot; {selectedProject.property_type} &middot; ${selectedProject.monthly_rent}/mo</p>
              </div>
              <StatusBadge status={selectedProject.status} />
            </div>

            <WalkthroughBanner
              sectionKey="project-detail"
              icon="🏠"
              color="#00FFFF"
              title="What is this page?"
              body={
                <>
                  <p style={{ margin: "0 0 8px" }}>This is your <strong>project view</strong> for this property. Everything Moovin Forward is doing for this unit lives here.</p>
                  <p style={{ margin: "0 0 8px" }}>Scroll down to see <strong>photos</strong>, <strong>materials</strong>, <strong>invoices</strong>, and a <strong>timeline of updates</strong>. Every photo is timestamped + watermarked — your unimpeachable proof of work.</p>
                  <p style={{ margin: 0 }}>If our crew flags an issue mid-job, you'll get an email with a one-tap reply link. <strong>You don't need to keep this page open.</strong></p>
                </>
              }
            />

            {/* Shared View Banner */}
            {selectedProject.is_shared_view && (
              <div className="shared-view-banner" data-testid="shared-view-banner">
                <span className="shared-role-tag">{selectedProject.shared_role === "regional_manager" ? "Regional Manager" : "Leasing Agent"}</span>
                <span>Shared with you — Read-only access</span>
              </div>
            )}

            {selectedProject.admin_notes && (
              <div className="detail-section">
                <h2>Team Notes</h2>
                <p className="admin-notes">{selectedProject.admin_notes}</p>
              </div>
            )}

            <div className="detail-section" data-testid="messages-section">
              <PortalMessages projectId={selectedProject.project_id} />
            </div>

            {/* Property Access Notes for Crew */}
            <div className="detail-section" data-testid="property-notes-section">
              <h2>Property Access & Instructions</h2>
              <p className="notes-hint">These notes are shared with your assigned crew members so they know how to access your property safely.</p>
              <div className="prop-notes-form">
                <div className="prop-notes-row">
                  <div className="prop-note-field">
                    <label>Entry / Gate Code</label>
                    <input type="text" value={propNotes.entry_code} onChange={e => setPropNotes(p => ({...p, entry_code: e.target.value}))} placeholder="e.g., #1234 or lockbox code" className="prop-note-input" data-testid="note-entry-code" />
                  </div>
                  <div className="prop-note-field">
                    <label>Security Alarm Code</label>
                    <input type="text" value={propNotes.alarm_code} onChange={e => setPropNotes(p => ({...p, alarm_code: e.target.value}))} placeholder="e.g., 5678 + disarm at keypad" className="prop-note-input" data-testid="note-alarm-code" />
                  </div>
                </div>
                <div className="prop-notes-row">
                  <div className="prop-note-field">
                    <label>Pets / Animals on Property</label>
                    <input type="text" value={propNotes.pets} onChange={e => setPropNotes(p => ({...p, pets: e.target.value}))} placeholder="e.g., Dog in backyard — do not open gate" className="prop-note-input" data-testid="note-pets" />
                  </div>
                  <div className="prop-note-field">
                    <label>Parking Instructions</label>
                    <input type="text" value={propNotes.parking_instructions} onChange={e => setPropNotes(p => ({...p, parking_instructions: e.target.value}))} placeholder="e.g., Park in visitor spot #12" className="prop-note-input" data-testid="note-parking" />
                  </div>
                </div>
                <div className="prop-note-field" style={{width: "100%"}}>
                  <label>Special Instructions</label>
                  <textarea value={propNotes.special_instructions} onChange={e => setPropNotes(p => ({...p, special_instructions: e.target.value}))} placeholder="Any other important information for the crew..." rows={3} className="prop-note-input" data-testid="note-special" />
                </div>
                <button onClick={savePropertyNotes} disabled={notesSaving} className="prop-notes-save" data-testid="save-notes-btn">{notesSaving ? "Saving..." : notesSaved ? "Saved!" : "Save Notes"}</button>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="detail-section cancellation-policy" data-testid="cancellation-policy">
              <h2>Scheduling & Cancellation Policy</h2>
              <div className="policy-items">
                <div className="policy-item policy-ok">
                  <strong>Cancel with 1+ hours notice</strong>
                  <span>No charge. We appreciate advance notice so we can reassign the crew to another job site.</span>
                </div>
                <div className="policy-item policy-warn">
                  <strong>Cancel less than 1 hour before scheduled arrival</strong>
                  <span>50% of the scheduled service cost will be billed. This compensates for lost time when we cannot rebook the crew member to another assignment.</span>
                </div>
                <div className="policy-item policy-critical">
                  <strong>Cancel after crew has arrived or work has started</strong>
                  <span>100% of the scheduled service cost will be billed for the full allotted time the crew member was scheduled at your property.</span>
                </div>
              </div>
              <p className="policy-note">To reschedule, please contact us at least 24 hours in advance at <a href="tel:+18173818831">(817) 381-8831</a> or through this portal.</p>
            </div>

            {/* Payment Status Banner */}
            {paymentStatus && (
              <div className={`payment-banner payment-${paymentStatus}`} data-testid="payment-banner">
                {paymentStatus === "checking" && "Verifying your payment..."}
                {paymentStatus === "paid" && "Payment confirmed! Thank you. 48-hour procurement window has started."}
                {paymentStatus === "cancelled" && "Payment was cancelled. You can try again anytime."}
                {paymentStatus === "timeout" && "Payment verification timed out. If you completed the payment, it will be reflected shortly."}
              </div>
            )}

            {/* Financial Summary */}
            <div className="detail-section financial-section" data-testid="financial-section">
              <h2>Financial Summary</h2>
              <div className="financial-grid">
                <div className="fin-card" data-testid="fin-rent">
                  <span className="fin-label">Monthly Rent</span>
                  <span className="fin-value">${selectedProject.financial?.monthly_rent?.toLocaleString() || "0"}</span>
                </div>
                <div className="fin-card" data-testid="fin-stab-fee">
                  <span className="fin-label">Stabilization Fee</span>
                  <span className="fin-value">${selectedProject.financial?.stabilization_fee?.toLocaleString() || "0"}</span>
                  <span className="fin-note">Locked per-unit fee (due upon completion)</span>
                </div>
                <div className="fin-card" data-testid="fin-invoiced">
                  <span className="fin-label">Total Invoiced</span>
                  <span className="fin-value">${selectedProject.financial?.total_invoiced?.toLocaleString() || "0"}</span>
                </div>
                <div className="fin-card" data-testid="fin-paid">
                  <span className="fin-label">Total Paid</span>
                  <span className="fin-value fin-paid">${selectedProject.financial?.total_paid?.toLocaleString() || "0"}</span>
                </div>
              </div>

              {/* Payment Model Explanation */}
              <div className="payment-model-info" data-testid="payment-model">
                <h4>How Our Payment Model Works</h4>
                <div className="payment-steps">
                  <div className="payment-step">
                    <span className="payment-num">1</span>
                    <div>
                      <strong>Activation Capital (50%)</strong>
                      <p>Covers all materials — paint, flooring, fixtures. Funded upfront before work begins. Every dollar is itemized in your Line-Itemized Packet.</p>
                    </div>
                  </div>
                  <div className="payment-step">
                    <span className="payment-num">2</span>
                    <div>
                      <strong>Stabilization Fee (50%)</strong>
                      <p>Your locked per-unit stabilization fee — covers labor, documentation, and compliance. Due only after your final sign-off walk-through. 72-hour settlement.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoices & Receipts */}
            <div className="detail-section" data-testid="invoices-section">
              <h2>Invoices & Receipts</h2>
              <WalkthroughBanner
                sectionKey="invoices"
                icon="💳"
                color="#A78BFA"
                title="What is this?"
                body={
                  <>
                    <p style={{ margin: "0 0 6px" }}>Every invoice we send appears here. Pay any open invoice with one tap via Stripe (secure — we never see your card).</p>
                    <p style={{ margin: 0 }}>Receipts are stored forever. Use the <strong>Financial Summary</strong> above to see year-to-date totals for your taxes.</p>
                  </>
                }
              />
              {selectedProject.invoices?.length > 0 ? (
                <div className="invoices-list">
                  {selectedProject.invoices.map((inv, i) => (
                    <div key={i} className="invoice-item" data-testid={`invoice-${i}`}>
                      <div className="invoice-left">
                        <strong>{inv.description}</strong>
                        <span className="invoice-date">{new Date(inv.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="invoice-right">
                        <span className="invoice-amount">${inv.amount?.toFixed(2)}</span>
                        <span className={`invoice-status status-${(inv.status || "pending").toLowerCase()}`}>{inv.status || "Pending"}</span>
                        {!selectedProject.is_shared_view && (inv.status || "Pending").toLowerCase() !== "paid" && (
                          <button onClick={() => handlePayInvoice(inv)} disabled={payingInvoice === inv.invoice_id} className="pay-now-btn" data-testid={`pay-now-${i}`}>
                            {payingInvoice === inv.invoice_id ? "Processing..." : "Pay Now"}
                          </button>
                        )}
                      </div>
                      {inv.paid_at && <span className="invoice-paid-date">Paid: {new Date(inv.paid_at).toLocaleDateString()}</span>}
                    </div>
                  ))}
                </div>
              ) : <p className="empty-state">No invoices yet. Invoices will appear here once the scope is approved and Activation Capital is funded.</p>}

              {/* Payment Legal Disclaimers */}
              <div className="payment-legal" data-testid="payment-legal">
                <h4>Payment Terms & Transparency</h4>
                <div className="legal-items">
                  <div className="legal-item">
                    <strong>Activation Capital (Materials)</strong>
                    <p>Your Activation Capital funds ALL materials for your unit — paint, flooring, fixtures, hardware, and supplies. Every dollar is itemized with receipts in your Line-Itemized Packet. Moovin Forward does not mark up materials. Once purchased, materials are non-refundable as they are procured specifically for your unit per the approved scope of work.</p>
                  </div>
                  <div className="legal-item">
                    <strong>Stabilization Fee (Labor & Documentation)</strong>
                    <p>Your Stabilization Fee is a locked per-unit rate agreed at your walk-through (minimum $200). It covers all labor, project coordination, documentation (before/after photography, Line-Itemized Packet), and NSPIRE-Ready Compliance Checklist. This fee is due only after your final sign-off walk-through and approval of completed work. Settlement: within 72 hours of sign-off.</p>
                  </div>
                  <div className="legal-item">
                    <strong>Payment Processing</strong>
                    <p>Payments are processed securely through Stripe. ACH bank transfer is recommended for lower fees ($5 flat vs. 2.9% + $0.30 for card payments). Payments settle to our business account next business day. A 48-hour procurement window begins after payment clears — materials are purchased and staged before crew mobilization.</p>
                  </div>
                  <div className="legal-item">
                    <strong>Refund & Dispute Policy</strong>
                    <p>If work does not meet the approved scope, Moovin Forward will correct at no additional cost before final sign-off. Disputes on material costs can be reviewed against receipts in your Line-Itemized Packet. All billing inquiries should be directed to nivoomforward@gmail.com within 14 days of invoice issuance.</p>
                  </div>
                  <div className="legal-item">
                    <strong>TREC Independence Disclosure</strong>
                    <p>Moovin Forward is NOT a brokerage, property management company, or leasing agency. All fees relate exclusively to interior unit stabilization services — physical renovation and documentation. We do not list units, negotiate leases, collect rent, screen tenants, or manage properties.</p>
                  </div>
                  <div className="legal-item">
                    <strong>Stagnation Fee Notice</strong>
                    <p>If a unit is not accessible, utilities are not active, or the unit is not vacated as required under the "Before We Start" section, a Stagnation Fee of $75/day may be applied for delays attributable to the property owner. This fee begins 48 hours after the scheduled start date and is documented in writing before assessment.</p>
                  </div>
                </div>
                <p className="legal-footer">By submitting payment, you acknowledge these terms and confirm that you have reviewed and approved the scope of work for your property. All transactions are final upon sign-off. Moovin Forward recommends maintaining copies of all invoices and documentation for your records.</p>
              </div>
            </div>

            {/* Before We Start — Requirements */}
            <div className="detail-section requirements-section" data-testid="requirements-section">
              <h2>Before We Start</h2>
              <div className="req-grid">
                <div className="req-column">
                  <h3 className="req-heading req-owner">Owner Responsibilities</h3>
                  <div className="req-item" data-testid="req-materials"><span className="req-label">Materials</span><span className="req-desc">Owner provides or funds all materials and supplies</span></div>
                  <div className="req-item" data-testid="req-access"><span className="req-label">Unit Access</span><span className="req-desc">Keys, gate codes, and building access arranged</span></div>
                  <div className="req-item" data-testid="req-utilities"><span className="req-label">Utilities</span><span className="req-desc">Electric, water, and gas must be active in the unit</span></div>
                  <div className="req-item" data-testid="req-vacancy"><span className="req-label">Vacancy</span><span className="req-desc">Unit must be fully vacated — all personal property removed</span></div>
                  <div className="req-item" data-testid="req-scope"><span className="req-label">Scope Approval</span><span className="req-desc">Review and approve the proposed scope of work</span></div>
                </div>
                <div className="req-column">
                  <h3 className="req-heading req-us">What We Handle</h3>
                  <div className="req-item" data-testid="req-assessment"><span className="req-label">Assessment</span><span className="req-desc">Full property walk-through and scope development</span></div>
                  <div className="req-item" data-testid="req-labor"><span className="req-label">Labor</span><span className="req-desc">All coordination, scheduling, and execution</span></div>
                  <div className="req-item" data-testid="req-photos"><span className="req-label">Documentation</span><span className="req-desc">Before/after photos, Line-Itemized Packet, NSPIRE-Ready Checklist</span></div>
                  <div className="req-item" data-testid="req-marketing"><span className="req-label">Compliance</span><span className="req-desc">NSPIRE/HQS certification and habitability documentation</span></div>
                  <div className="req-item" data-testid="req-tracking"><span className="req-label">Tracking</span><span className="req-desc">Real-time progress updates, photos, and timeline through your portal</span></div>
                </div>
              </div>
            </div>

            {/* Habitability & Infestation Protocol — Mandatory Acknowledgment */}
            <div className="detail-section protocol-section" data-testid="habitability-protocol">
              <h2>Professional Habitability & Infestation Protocol</h2>
              <p className="protocol-subtitle">Mandatory acknowledgment required prior to site mobilization</p>

              <div className="protocol-block" data-testid="protocol-pest-free">
                <h4>1. Warranty of Pest-Free Status</h4>
                <p>The Property Owner/Receiver (the "Client") warrants and represents that each unit designated for interior stabilization is free of active infestations, including but not limited to <em>Cimex lectularius</em> (Bed Bugs), as of the date of site mobilization.</p>
                <p className="protocol-note">For multi-unit properties, the Client further warrants that all adjacent and vertically stacked units have been inspected and cleared of active infestations within 60 days of site mobilization.</p>
              </div>

              <div className="protocol-block" data-testid="protocol-appliances">
                <h4>2. Mandatory Appliance Removal for Sanitation</h4>
                <p>Each unit runs under one appliance mode: Mode 1 Full DFW Procurement, Mode 2 Owner Retention with an NSPIRE safety check, or Mode 3 Clear Unit. Under Mode 3, all appliances (refrigerators, ranges, dishwashers) are removed before Moovin Forward's arrival. Appliances are primary harborage zones for structural pests, and removal lets the Client perform extermination in a controlled environment. Gas connections are handled only by a licensed gas technician.</p>
                <p className="protocol-warning">Non-Compliance (Mode 3): Units with appliances remaining on-site will be flagged as "Not Ready for Production," and a Stagnation Fee may be applied.</p>
              </div>

              <div className="protocol-block" data-testid="protocol-discovery">
                <h4>3. Discovery & Work Suspension Clause</h4>
                <p>If evidence of an active infestation is discovered during interior stabilization:</p>
                <ul className="protocol-list">
                  <li><strong>Immediate Cessation:</strong> All work will be suspended to prevent cross-contamination of equipment and materials.</li>
                  <li><strong>Treatment Required:</strong> The Client must engage a Texas-licensed Structural Pest Control Service (SPCS) for professional treatment.</li>
                  <li><strong>Certification of Clearance:</strong> Work resumes only upon receipt of a Certified Clearance Report. Self-treated clearance is not accepted.</li>
                </ul>
              </div>

              <div className="protocol-block" data-testid="protocol-liability">
                <h4>4. Liability & Financial Indemnification</h4>
                <ul className="protocol-list">
                  <li><strong>Equipment Contamination:</strong> The Client shall be liable for all costs associated with professional heat-treatment or replacement of Moovin Forward tools, materials, or vehicles if contamination occurs due to a non-disclosed infestation.</li>
                  <li><strong>Loss of Use:</strong> Moovin Forward is not liable for project delays or holding costs resulting from the Client's failure to provide a pest-free environment.</li>
                </ul>
              </div>
            </div>

            {/* Project Timeline Tracker */}
            <div className="detail-section timeline-section" data-testid="timeline-section">

            {/* On-Site Team & Insurance */}
            {selectedProject.crew_on_site?.length > 0 && (
              <div className="detail-section" data-testid="onsite-team-section">
                <h2>On-Site Team & Insurance Verification</h2>
                <p className="onsite-team-note">All crew members are independent contractors (1099). Any damages caused during work are covered by THEIR insurance — not Moovin Forward's policy. Insurance documentation is on file for each team member below.</p>
                <div className="onsite-team-grid">
                  {selectedProject.crew_on_site.map((crew, i) => (
                    <div key={i} className={`onsite-crew-card ${crew.insurance_status === "submitted" ? "insured" : "uninsured"}`} data-testid={`crew-card-${i}`}>
                      <div className="crew-card-header">
                        <strong>{crew.name}</strong>
                        <span className={`ins-badge ${crew.insurance_status === "submitted" ? "ins-verified" : "ins-pending"}`}>
                          {crew.insurance_status === "submitted" ? "Insured" : "Pending"}
                        </span>
                      </div>
                      {crew.insurance_status === "submitted" ? (
                        <div className="crew-ins-details">
                          <div className="ins-detail-row"><span className="ins-label">Carrier:</span><span>{crew.insurance_company}</span></div>
                          <div className="ins-detail-row"><span className="ins-label">Policy:</span><span>{crew.policy_number}</span></div>
                          <div className="ins-detail-row"><span className="ins-label">Coverage:</span><span>{crew.coverage_amount}</span></div>
                          <div className="ins-detail-row"><span className="ins-label">Expires:</span><span>{crew.expiration_date}</span></div>
                          {crew.has_coi && <span className="coi-badge">COI on file</span>}
                        </div>
                      ) : (
                        <p className="ins-pending-text">Insurance information not yet submitted. Will be verified before on-site work begins.</p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="damage-liability-notice" data-testid="damage-liability-notice">
                  <strong>1099 Damage Liability Disclosure</strong>
                  <p>All crew members working on your property are independent contractors operating under their own insurance policies. In the event of property damage caused by a crew member during the course of stabilization work, the claim will be filed against that individual's insurance policy. Moovin Forward maintains its own General Liability coverage for coordination and oversight activities only. You may request a copy of any crew member's Certificate of Insurance (COI) at any time by contacting nivoomforward@gmail.com.</p>
                </div>
              </div>
            )}
              <h2>Project Timeline</h2>
              <div className="timeline-track" data-testid="timeline-track">
                {[
                  { key: "intake", label: "Intake", desc: "Walk-through & scope" },
                  { key: "materials", label: "48-Hr Procurement", desc: "Payment settled, materials purchased" },
                  { key: "execution", label: "Execution", desc: "Active stabilization" },
                  { key: "signoff", label: "Sign-Off", desc: "Walk-through & approval" }
                ].map((step, idx) => {
                  const phases = ["intake", "materials", "execution", "signoff"];
                  const currentIdx = phases.indexOf(selectedProject.phase || "");
                  const isComplete = idx < currentIdx || selectedProject.phase === "complete";
                  const isCurrent = idx === currentIdx;
                  return (
                    <div key={step.key} className={`timeline-step ${isComplete ? "step-complete" : ""} ${isCurrent ? "step-current" : ""}`} data-testid={`timeline-${step.key}`}>
                      <div className="step-marker">
                        {isComplete ? <span className="step-check">&#10003;</span> : <span className="step-num">{idx + 1}</span>}
                      </div>
                      {idx < 3 && <div className={`step-line ${isComplete ? "line-complete" : ""}`}></div>}
                      <span className="step-label">{step.label}</span>
                      <span className="step-desc">{step.desc}</span>
                    </div>
                  );
                })}
              </div>
              {selectedProject.phase === "complete" && (
                <div className="timeline-complete" data-testid="timeline-complete">Unit stabilized and delivered. Stabilization Fee due within 72 hours.</div>
              )}
              {selectedProject.phase === "materials" && selectedProject.execution_start && (
                <div className="procurement-countdown" data-testid="procurement-countdown">
                  48-Hour Procurement Window active. Materials are being purchased and staged. Crew mobilization target: {new Date(selectedProject.execution_start).toLocaleDateString()}.
                </div>
              )}
            </div>

            {/* Inspection Photos */}
            {selectedProject.photos?.length > 0 && (
              <div className="detail-section" data-testid="portal-photos-section">
                <h2>Inspection Documentation ({selectedProject.photos.length} photos)</h2>
                <WalkthroughBanner
                  sectionKey="photos"
                  icon="📸"
                  color="#FFB347"
                  title="What is this?"
                  body={
                    <>
                      <p style={{ margin: "0 0 6px" }}>Every photo and video our crew takes is <strong>timestamped, dated, and watermarked with this property's address</strong> — directly baked into the image. Nobody can edit it out.</p>
                      <p style={{ margin: 0 }}>Filter by <em>before</em>, <em>progress</em>, <em>issue</em>, or <em>after</em>. These photos are your unimpeachable proof of work for insurance, tax, or dispute purposes.</p>
                    </>
                  }
                />
                <p className="photos-intro">Timestamped before/after documentation for your records. Every photo is stored permanently.</p>
                <div className="portal-photo-grid">
                  {selectedProject.photos.map((photo, i) => {
                    const isVideo = photo.is_video || /\.(mp4|mov|webm|m4v)$/i.test(photo.original_filename || "");
                    return (
                      <div key={i} className="portal-photo-card" data-testid={`portal-photo-${i}`}>
                        <div className="portal-photo-img">
                          {isVideo ? (
                            <video src={`${BACKEND_URL}/api/files/${photo.storage_path}`} controls preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }} />
                          ) : (
                            <img src={`${BACKEND_URL}/api/files/${photo.storage_path}`} alt={photo.notes || photo.photo_type} loading="lazy" />
                          )}
                          <span className={`portal-photo-badge badge-${photo.photo_type}`}>{photo.photo_type.replace("-", " ")}{isVideo ? " · video" : ""}</span>
                        </div>
                        <div className="portal-photo-info">
                          {photo.notes && <p className="portal-photo-notes">{photo.notes}</p>}
                          <span className="portal-photo-meta">{new Date(photo.created_at).toLocaleString()} — {photo.uploaded_by}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Materials Used */}
            {selectedProject.materials?.length > 0 && (
              <div className="detail-section" data-testid="portal-materials-section">
                <h2>Materials Verification ({selectedProject.materials.filter(m => m.checked).length}/{selectedProject.materials.length} verified)</h2>
                <div className="portal-materials-list">
                  {selectedProject.materials.map((m, i) => (
                    <div key={i} className={`portal-material-item ${m.checked ? "verified" : ""}`} data-testid={`portal-material-${i}`}>
                      <span className={`portal-mat-check ${m.checked ? "on" : ""}`}>{m.checked ? "\u2713" : ""}</span>
                      <div className="portal-mat-info">
                        <span className="portal-mat-name">{m.name}</span>
                        {m.spec && <span className="portal-mat-spec">{m.spec}</span>}
                        {m.quantity && <span className="portal-mat-qty">Qty: {m.quantity}</span>}
                      </div>
                      {m.checked && <span className="portal-mat-verified">Verified by {m.checked_by}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progress Updates */}
            <div className="detail-section">
              <h2>Progress Updates</h2>
              {selectedProject.updates?.length > 0 ? (
                <div className="updates-list">
                  {selectedProject.updates.map((u, i) => (
                    <div key={i} className={`update-item ${u.phase ? `update-phase-${u.phase}` : ""}`} data-testid={`update-${i}`}>
                      <div className="update-header">
                        <span className="update-date">{new Date(u.created_at).toLocaleDateString()}{u.posted_by ? ` — ${u.posted_by}` : ""}</span>
                        {u.phase && <span className="update-phase-tag">{u.phase}</span>}
                      </div>
                      <p>{u.text || u.message}</p>
                      {u.image_url && <img src={u.image_url} alt="Progress" className="update-photo" data-testid={`update-photo-${i}`} />}
                    </div>
                  ))}
                </div>
              ) : <p className="empty-state">No updates yet. Our team will post progress photos and status updates here as work begins.</p>}
            </div>

            {/* Documents */}
            <div className="detail-section">
              <h2>Documents</h2>
              {selectedProject.documents?.length > 0 ? (
                <div className="docs-list">
                  {selectedProject.documents.map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noreferrer" className="doc-item" data-testid={`doc-${i}`}>
                      <span className="doc-name">{d.name}</span>
                      <span className="doc-date">{new Date(d.created_at).toLocaleDateString()}</span>
                    </a>
                  ))}
                </div>
              ) : <p className="empty-state">No documents uploaded yet. Your Line-Itemized Packet and compliance documents will appear here upon project completion.</p>}
            </div>

            {/* Share Access (Owner only) */}
            {!selectedProject.is_shared_view && (
              <div className="detail-section share-section" data-testid="share-access-section">
                <h2>Share Access</h2>
                <p className="share-desc">Grant read-only access to Leasing Agents or Regional Managers. They can view project status, photos, documents, and crew info — but cannot make payments or modifications.</p>
                <div className="share-form" data-testid="share-form">
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={e => setShareEmail(e.target.value)}
                    placeholder="Enter email address..."
                    className="share-email-input"
                    data-testid="share-email-input"
                  />
                  <select value={shareRole} onChange={e => setShareRole(e.target.value)} className="share-role-select" data-testid="share-role-select">
                    <option value="leasing_agent">Leasing Agent</option>
                    <option value="regional_manager">Regional Manager</option>
                  </select>
                  <button onClick={handleShareAccess} disabled={sharing || !shareEmail.trim()} className="share-btn" data-testid="share-btn">
                    {sharing ? "Sharing..." : "Share"}
                  </button>
                </div>
                {selectedProject.shared_users?.length > 0 && (
                  <div className="shared-users-list" data-testid="shared-users-list">
                    {selectedProject.shared_users.map(su => (
                      <div key={su.share_id} className="shared-user-item" data-testid={`shared-user-${su.share_id}`}>
                        <div className="shared-user-info">
                          <span className="shared-user-email">{su.shared_with_email}</span>
                          <span className={`shared-user-role role-${su.role}`}>{su.role === "regional_manager" ? "Regional Manager" : "Leasing Agent"}</span>
                        </div>
                        <button onClick={() => handleRevokeAccess(su.share_id)} className="revoke-btn" data-testid={`revoke-${su.share_id}`}>Revoke</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* FAQ */}
            <div className="detail-section faq-section" data-testid="faq-section">
              <h2>Common Questions</h2>
              <div className="faq-list">
                <div className="faq-item" data-testid="faq-1">
                  <h4>What does my Activation Capital cover?</h4>
                  <p>100% of materials — paint, flooring, fixtures, hardware, supplies. Every dollar is itemized with receipts in your Line-Itemized Packet. No markup on materials.</p>
                </div>
                <div className="faq-item" data-testid="faq-2">
                  <h4>When is the Stabilization Fee due?</h4>
                  <p>Only after your final sign-off walk-through. You inspect the completed unit, approve the work, and settle within 72 hours. If you're not satisfied, we fix it before you pay.</p>
                </div>
                <div className="faq-item" data-testid="faq-3">
                  <h4>How long does a typical unit take?</h4>
                  <p>Standard units: 14-21 days depending on scope. We'll give you an exact timeline in the scope of work after the initial walk-through.</p>
                </div>
                <div className="faq-item" data-testid="faq-4">
                  <h4>What documentation do I receive?</h4>
                  <p>A complete Line-Itemized Packet with: itemized material costs with receipts, labor allocation breakdown, before/after photo documentation, and an NSPIRE-Ready Checklist (if applicable). This is court-grade documentation.</p>
                </div>
                <div className="faq-item" data-testid="faq-5">
                  <h4>Are you a property management company?</h4>
                  <p>No. We are an interior unit stabilization firm. We do not list units, negotiate leases, collect rent, or manage tenants. We stabilize the physical asset and return it to you market-ready.</p>
                </div>
                <div className="faq-item" data-testid="faq-6">
                  <h4>What if an issue is found during work?</h4>
                  <p>Our crew documents every issue with photos and notes immediately. You'll see it in the Inspection Documentation section above with the "Issue Found" tag. We'll discuss the resolution with you before proceeding.</p>
                </div>
                <div className="faq-item" data-testid="faq-7">
                  <h4>Can I visit the unit during work?</h4>
                  <p>Yes. Coordinate with our team for a scheduled visit. We recommend the sign-off walk-through as the best time to inspect the completed work.</p>
                </div>
                <div className="faq-item" data-testid="faq-8">
                  <h4>What happens if I need to dispute a charge?</h4>
                  <p>Every charge is backed by a receipt in your Line-Itemized Packet. If there's a discrepancy, contact us and we'll walk through the documentation together. Transparency is the foundation of our model.</p>
                </div>
              </div>
            </div>

            {/* Client Notes */}
            <div className="detail-section">
              <h2>Your Notes</h2>
              <p className="client-notes">{selectedProject.notes || "No notes provided."}</p>
            </div>
          </div>
        ) : (
          // Projects List View
          <div className="projects-view">
            <div className="projects-header">
              <h1 data-testid="projects-title">Your Properties</h1>
              <div className="projects-header-actions">
                <button onClick={() => setShowServices(p => !p)} className="services-toggle-btn" data-testid="services-toggle-btn">
                  {showServices ? "Hide" : "View"} Services
                </button>
                <button onClick={() => setShowAddForm(true)} className="add-btn" data-testid="add-property-btn">+ Add Property</button>
              </div>
            </div>

            {/* Services & Capabilities — Gated for Authenticated Users */}
            {showServices && (
              <div className="services-overview" data-testid="services-overview">
                <div className="services-header-bar">
                  <div>
                    <h2 className="services-title">Moovin Forward — Services & Capabilities</h2>
                    <p className="services-subtitle">Interior Unit Stabilization | DFW Metropolitan Area</p>
                  </div>
                  <button onClick={() => { setShowServices(false); localStorage.setItem("mf_services_seen", "1"); }} className="services-dismiss" data-testid="services-dismiss">&times;</button>
                </div>

                {/* Who We Are */}
                <div className="svc-section" data-testid="svc-who-we-are">
                  <h3>Who We Are</h3>
                  <p>Moovin Forward is a DFW-based professional firm specializing in interior unit stabilization for multifamily and single-family rental properties across the Dallas-Fort Worth metropolitan area. We are <strong>not</strong> a property management company, brokerage, or general contractor. We are a precision stabilization firm built for institutional clients, court-appointed receivers, and portfolio operators who need speed, transparency, and documentation they can take to court.</p>
                </div>

                {/* The ROI Stabilization Model */}
                <div className="svc-section" data-testid="svc-roi-model">
                  <h3>The ROI Stabilization Model</h3>
                  <p className="svc-model-intro">Our proprietary 50/50 transparency model ensures every dollar is accounted for and every project is performance-based.</p>
                  <div className="svc-model-grid">
                    <div className="svc-model-card">
                      <span className="svc-model-num">1</span>
                      <div>
                        <strong>Activation Capital (50%)</strong>
                        <p>Covers 100% of materials — paint, flooring, fixtures, hardware. Every dollar is itemized with receipts. Zero markup. This is not our fee — it goes directly into your property.</p>
                      </div>
                    </div>
                    <div className="svc-model-card">
                      <span className="svc-model-num">2</span>
                      <div>
                        <strong>Stabilization Fee (50%)</strong>
                        <p>A locked per-unit stabilization fee agreed at your walk-through. Covers labor, coordination, documentation, and NSPIRE compliance. Due only after your signed walk-through approval. 72-hour settlement.</p>
                      </div>
                    </div>
                  </div>
                  <p className="svc-model-note">Every project produces a <strong>Line-Itemized Packet</strong> — court-grade documentation with receipts, before/after photos, NSPIRE-Ready Checklist, and signed approvals.</p>
                </div>

                {/* Service Tiers */}
                <div className="svc-section" data-testid="svc-tiers">
                  <h3>Service Tiers — Precision Wave Strategy</h3>
                  <div className="svc-tiers-grid">
                    <div className="svc-tier-card">
                      <div className="svc-tier-header tier-48">
                        <span className="svc-tier-badge">48-HOUR</span>
                        <h4>Rapid Recovery</h4>
                      </div>
                      <p>Light swift-turn — hardware swaps, paint touch-up, punch list items. Ideal for units that need a quick refresh between tenants.</p>
                      <span className="svc-tier-range">$800 — $1,500</span>
                    </div>
                    <div className="svc-tier-card svc-tier-featured">
                      <div className="svc-tier-header tier-14">
                        <span className="svc-tier-badge">14-DAY</span>
                        <h4>Volume Stabilization</h4>
                      </div>
                      <p>Full interior stabilization for multi-unit portfolios. Our most common engagement — designed for property managers turning 5-50+ units.</p>
                      <span className="svc-tier-range">$2,000 — $3,500</span>
                    </div>
                    <div className="svc-tier-card">
                      <div className="svc-tier-header tier-21">
                        <span className="svc-tier-badge">21-DAY</span>
                        <h4>Heavy Recovery</h4>
                      </div>
                      <p>Deep restoration for legacy or high-vacancy units. Full scope — flooring, paint, fixtures, cabinets, countertops. Receivership-grade execution.</p>
                      <span className="svc-tier-range">$3,500 — $5,500+</span>
                    </div>
                  </div>
                </div>

                {/* What You Get */}
                <div className="svc-section" data-testid="svc-what-you-get">
                  <h3>What Every Client Receives</h3>
                  <div className="svc-deliverables-grid">
                    <div className="svc-del-item">
                      <span className="svc-del-icon">1</span>
                      <div>
                        <strong>Real-Time Portal Access</strong>
                        <p>Track every project in real time — status updates, photos, documents, invoices, and crew info. Share read-only access with your Leasing Agents and Regional Managers.</p>
                      </div>
                    </div>
                    <div className="svc-del-item">
                      <span className="svc-del-icon">2</span>
                      <div>
                        <strong>Court-Grade Documentation</strong>
                        <p>Line-Itemized Packet with receipts, before/after photography, NSPIRE-Ready Checklist, and signed commencement/completion approvals. Built for audits, courts, and compliance reviews.</p>
                      </div>
                    </div>
                    <div className="svc-del-item">
                      <span className="svc-del-icon">3</span>
                      <div>
                        <strong>Insured & Verified Crew</strong>
                        <p>Every crew member carries their own General Liability insurance. Certificates of Insurance (COI) are on file and visible in your portal. 1099 damage liability is on them — not you.</p>
                      </div>
                    </div>
                    <div className="svc-del-item">
                      <span className="svc-del-icon">4</span>
                      <div>
                        <strong>48-Hour Procurement Window</strong>
                        <p>After payment settles, materials are purchased within 48 hours from approved vendors (Sherwin-Williams, HD Supply, Ferguson). Crew mobilizes Day 3. No delays, no excuses.</p>
                      </div>
                    </div>
                    <div className="svc-del-item">
                      <span className="svc-del-icon">5</span>
                      <div>
                        <strong>Secure Stripe Payments</strong>
                        <p>Pay invoices directly through the portal via ACH or card. Every payment is tracked, receipted, and tied to a specific unit and scope of work.</p>
                      </div>
                    </div>
                    <div className="svc-del-item">
                      <span className="svc-del-icon">6</span>
                      <div>
                        <strong>Team Access Controls</strong>
                        <p>Grant your Leasing Agents and Regional Managers read-only access to any project. Full visibility across your management hierarchy — no blind spots.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scalability */}
                <div className="svc-section" data-testid="svc-scalability">
                  <h3>Built for Scale</h3>
                  <p>Moovin Forward is not a one-crew operation. Our systems are designed to scale from 5 units to 50+ units per month without sacrificing quality, documentation, or accountability.</p>
                  <div className="svc-scale-points">
                    <div className="svc-scale-item">
                      <strong>Flat-Fee Labor Model</strong>
                      <p>Crew members are paid per-unit flat rates — costs stay predictable regardless of volume. No hourly overruns, no surprise labor bills.</p>
                    </div>
                    <div className="svc-scale-item">
                      <strong>Property-Specific Accounts</strong>
                      <p>Material procurement is tracked per-property with dedicated cash accounts. Clean audit trails for every dollar across every unit.</p>
                    </div>
                    <div className="svc-scale-item">
                      <strong>Crew Network Expansion</strong>
                      <p>Verified 1099 contractors with signed agreements, insurance on file, and damage liability acknowledgments. We can deploy additional crews within 48 hours of demand.</p>
                    </div>
                    <div className="svc-scale-item">
                      <strong>Technology-Driven Operations</strong>
                      <p>Every project flows through our platform — from intake to sign-off. Photo documentation, materials verification, payroll tracking, and client reporting are automated. More units, same precision.</p>
                    </div>
                  </div>
                </div>

                {/* Coming Soon */}
                <div className="svc-section svc-coming-soon" data-testid="svc-coming-soon">
                  <h3>On the Horizon</h3>
                  <p className="svc-coming-intro">As we scale, our platform and service offerings grow with you:</p>
                  <div className="svc-future-grid">
                    <div className="svc-future-item"><strong>Automated Progress Alerts</strong><p>Email and SMS notifications at every phase change — no more checking in manually.</p></div>
                    <div className="svc-future-item"><strong>Receivership Portfolio Management</strong><p>Dedicated workflows for court-appointed properties with compliance reporting built in.</p></div>
                    <div className="svc-future-item"><strong>Multi-Property Dashboards</strong><p>Portfolio-level views for operators managing 20+ units — aggregate status, spend, and timelines at a glance.</p></div>
                    <div className="svc-future-item"><strong>Vendor Marketplace</strong><p>Pre-negotiated pricing with approved suppliers. Volume discounts passed directly to you.</p></div>
                  </div>
                </div>

                {/* CTA */}
                <div className="svc-cta" data-testid="svc-cta">
                  <p>Ready to stabilize your next unit? Submit a property above or contact us at <strong>nivoomforward@gmail.com</strong> | <strong>moovinforward.org</strong></p>
                </div>
              </div>
            )}

            {showAddForm && (
              <form onSubmit={handleAddProperty} className="add-form" data-testid="add-property-form">
                <h2>Submit a Property for Stabilization</h2>
                <p className="form-guide">Fill in your property details below. Once submitted, our team will schedule a free on-site walk-through and send you a detailed scope with line items and timeline.</p>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Street Address *</label>
                    <input placeholder="e.g. 1234 Main St" required value={newProperty.address} onChange={(e) => setNewProperty({...newProperty, address: e.target.value})} data-testid="prop-address" />
                  </div>
                  <div className="form-field">
                    <label>City</label>
                    <input placeholder="e.g. Dallas, Fort Worth" value={newProperty.city} onChange={(e) => setNewProperty({...newProperty, city: e.target.value})} data-testid="prop-city" />
                  </div>
                  <div className="form-field">
                    <label>Unit Number</label>
                    <input placeholder="e.g. 204, Building A" value={newProperty.unit_number} onChange={(e) => setNewProperty({...newProperty, unit_number: e.target.value})} data-testid="prop-unit" />
                  </div>
                  <div className="form-field">
                    <label>Property Type</label>
                    <select value={newProperty.property_type} onChange={(e) => setNewProperty({...newProperty, property_type: e.target.value})} data-testid="prop-type">
                      <option value="">Select type...</option>
                      <option value="Single Family">Single Family</option>
                      <option value="Multi-Family">Multi-Family (2-4 units)</option>
                      <option value="Apartment">Apartment Complex (5+)</option>
                      <option value="Commercial">Commercial</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Current Monthly Rent ($)</label>
                    <input placeholder="e.g. 1800" type="number" value={newProperty.monthly_rent} onChange={(e) => setNewProperty({...newProperty, monthly_rent: e.target.value})} data-testid="prop-rent" />
                    <span className="form-hint">Used internally to finalize your locked per-unit Stabilization Fee at walk-through</span>
                  </div>
                </div>
                <div className="form-field">
                  <label>Notes for Our Team</label>
                  <textarea placeholder="Condition details, access instructions, special requirements, number of units if multi-property..." value={newProperty.notes} onChange={(e) => setNewProperty({...newProperty, notes: e.target.value})} rows="3" data-testid="prop-notes" />
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-prop-btn" data-testid="submit-property-btn">Submit Property</button>
                  <button type="button" onClick={() => setShowAddForm(false)} className="cancel-btn">Cancel</button>
                </div>
              </form>
            )}

            {projects.length > 0 ? (
              <div className="projects-list">
                {projects.map((p) => (
                  <button key={p.project_id} onClick={() => loadProjectDetail(p.project_id)} className="project-card" data-testid={`project-${p.project_id}`}>
                    <div className="project-card-top">
                      <h3>{p.address}{p.unit_number ? `, #${p.unit_number}` : ""}</h3>
                      <div className="project-card-badges">
                        {p._isShared && <span className="shared-badge" data-testid="shared-badge">{p.shared_access_role === "regional_manager" ? "RM" : "LA"}</span>}
                        <StatusBadge status={p.status} />
                      </div>
                    </div>
                    <div className="project-card-meta">
                      {p._isShared && <span className="shared-by-text">Shared by {p.shared_by}</span>}
                      <span>{p.city || "DFW"}</span>
                      <span>{p.property_type || "—"}</span>
                      <span>{p.monthly_rent ? `$${p.monthly_rent}/mo` : "—"}</span>
                      <span>{new Date(p.created_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : !showAddForm && (
              <div className="empty-projects" data-testid="empty-projects">
                <h2>Welcome to Moovin Forward</h2>
                <p className="empty-intro">Your property stabilization portal. Here's how it works:</p>
                <div className="onboard-steps">
                  <div className="onboard-step"><span className="onboard-num">1</span><div><strong>Submit Your Property</strong><p>Enter the address, unit info, and current rent. No commitment — just gets us started.</p></div></div>
                  <div className="onboard-step"><span className="onboard-num">2</span><div><strong>We Walk It</strong><p>Our team schedules a free on-site assessment and sends you a detailed scope with line items and timeline.</p></div></div>
                  <div className="onboard-step"><span className="onboard-num">3</span><div><strong>You Approve, We Execute</strong><p>Green-light the plan. Fund the Activation Capital. Track progress, documents, and invoices right here in your portal.</p></div></div>
                </div>
                <button onClick={() => setShowAddForm(true)} className="add-btn" data-testid="start-btn">+ Submit Your First Property</button>
                <div className="portal-policy-reminder" data-testid="portal-policy-reminder">
                  <h3>Important — Property Access & Cancellation</h3>
                  <p><strong>Property Notes:</strong> Once your project is active, add entry codes, alarm codes, pet info, and parking instructions so our crew can access your property safely.</p>
                  <p><strong>Cancellations:</strong> 1+ hour notice = no charge. Less than 1 hour = 50% billed. After crew arrives = 100% billed. Reschedule 24 hours in advance at <a href="tel:+18173818831">(817) 381-8831</a>.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Portal;
